/**
 * SHX shape-code interpreter. Walks a single glyph's byte stream and
 * emits a `ShxGlyph` with a flat array of line / arc strokes in font
 * units.
 *
 * Opcode table (unifont 1.0 and 2.0):
 *
 * | Opcode | Meaning                                                 | 1.0 | 2.0 |
 * |--------|---------------------------------------------------------|-----|-----|
 * | 0x00   | end of shape                                            |  Y  |  Y  |
 * | 0x01   | pen down                                                |  Y  |  Y  |
 * | 0x02   | pen up                                                  |  Y  |  Y  |
 * | 0x03 n | divide scale by n                                       |  Y  |  Y  |
 * | 0x04 n | multiply scale by n                                     |  Y  |  Y  |
 * | 0x05   | push current (x,y)                                      |  Y  |  Y  |
 * | 0x06   | pop to (x,y)                                            |  Y  |  Y  |
 * | 0x07 h l | subshape: 2-byte BE codepoint                         |  Y  |  Y  |
 * | 0x08 dx dy | signed displacement (int8)                          |  Y  |  Y  |
 * | 0x09 dx dy ... 0,0 | sequence of displacements terminated by 0,0 |  Y  |  Y  |
 * | 0x0A r oct | octant arc (radius, octant byte)                    |  Y  |  Y  |
 * | 0x0B ... | fractional arc (5 bytes)                              |  —  |  Y  |
 * | 0x0C dx dy bulge | bulge arc (int8 / 127)                        |  Y  |  Y  |
 * | 0x0D dx dy bulge ... (0,0,_) | polyline bulge arcs                |  Y  |  Y  |
 * | 0x0E     | vertical-only flag: skip next op in horizontal mode   |  Y  |  Y  |
 * | 0x0F     | reserved; skip 1 byte                                 |  Y  |  Y  |
 * | 0x10-0xFF | direction-length vector (high nibble = len, low = dir of 22.5° step) |  Y  |  Y  |
 *
 * `formatVersion` is passed in from `parseShxFont` and gates the 0x0B
 * handling — in v1.0 the opcode is reserved and must not be decoded
 * with the v2.0 5-byte fractional-arc layout (a v1.0 file that
 * contains it is almost certainly corrupted, so we skip defensively
 * rather than risking bogus geometry).
 *
 * The interpreter mirrors AutoCAD's behaviour as documented in the
 * Autodesk "Customization Guide / Creating Shape Files" reference.
 * Specifically:
 * - `scale` starts at 1 and is mutated by 0x03 / 0x04.
 * - The stack is a local (x,y) snapshot, not a full-state push.
 * - Subshape recursion is limited to depth 10 (sufficient for all
 *   known AutoCAD fonts and catches circular references).
 */

import type { ShxFormatVersion, ShxGlyph, ShxStroke } from "./types";

/** SHX opcodes — documented for readability. */
export const SHX_OP = {
  END: 0x00,
  PEN_DOWN: 0x01,
  PEN_UP: 0x02,
  SCALE_DIVIDE: 0x03,
  SCALE_MULTIPLY: 0x04,
  PUSH: 0x05,
  POP: 0x06,
  SUBSHAPE: 0x07,
  DISPLACEMENT: 0x08,
  DISPLACEMENT_SEQ: 0x09,
  OCTANT_ARC: 0x0a,
  FRACTIONAL_ARC: 0x0b,
  BULGE_ARC: 0x0c,
  POLYLINE_ARC: 0x0d,
  VERTICAL_ONLY: 0x0e,
  RESERVED_0F: 0x0f,
} as const;

/**
 * Error thrown by the interpreter. Caught by `parseShxFont` and wrapped
 * into a `ShxParseResult` failure. Kept as a plain object for the same
 * reasons as `CursorTruncationError`.
 */
export type InterpreterError = {
  code: "bad-shape" | "subshape-cycle" | "unknown-opcode" | "truncated";
  message: string;
};

function interpreterError(
  code: InterpreterError["code"],
  message: string,
): InterpreterError {
  return { code, message };
}

/** Convert an unsigned byte to a signed int8 (two's complement). */
export function toSignedInt8(byte: number): number {
  return byte > 127 ? byte - 256 : byte;
}

/** Translate a stroke by `(tx, ty)` and multiply its geometry by `scale`. */
function translateAndScaleStroke(
  stroke: ShxStroke,
  tx: number,
  ty: number,
  scale: number,
): ShxStroke {
  if (stroke.kind === "line") {
    return {
      kind: "line",
      x1: tx + stroke.x1 * scale,
      y1: ty + stroke.y1 * scale,
      x2: tx + stroke.x2 * scale,
      y2: ty + stroke.y2 * scale,
    };
  }
  return {
    kind: "arc",
    cx: tx + stroke.cx * scale,
    cy: ty + stroke.cy * scale,
    r: stroke.r * scale,
    startAngle: stroke.startAngle,
    endAngle: stroke.endAngle,
    ccw: stroke.ccw,
  };
}

/**
 * Build an octant arc (opcode 0x0A).
 *
 * Octant byte layout:
 * - bit 7 (0x80) = direction flag; 1 = CCW, 0 = CW (AutoCAD convention
 *   is that positive span is CCW; the "negative span" is encoded via
 *   the sign bit).
 * - bits 4..6 (0x70 >> 4) = number of octants spanned (1..7; 0 is
 *   reserved and treated as 8 → full circle, matching AutoCAD).
 * - bits 0..2 (0x07) = start octant index 0..7, where octant 0 starts
 *   along +X and each successive octant advances CCW by 45°.
 *
 * The emitted arc is centred such that the endpoint of the previous
 * drawing is on the arc circumference — i.e. the interpreter passes
 * the current pen position as the **start** of the arc, not the
 * centre. The centre is computed by stepping back by one radius along
 * the normal to the tangent at the start.
 */
export function buildOctantArc(
  startX: number,
  startY: number,
  radius: number,
  octByte: number,
): { stroke: ShxStroke & { kind: "arc" }; endX: number; endY: number } {
  const ccw = (octByte & 0x80) !== 0;
  const spanOctants = (octByte & 0x70) >> 4 || 8;
  const startOctant = octByte & 0x07;

  const startAngle = (startOctant * Math.PI) / 4;
  const spanRadians = (spanOctants * Math.PI) / 4;
  const endAngle = ccw ? startAngle + spanRadians : startAngle - spanRadians;

  // The pen position is on the circumference at startAngle; derive the
  // centre by stepping back by radius.
  const cx = startX - Math.cos(startAngle) * radius;
  const cy = startY - Math.sin(startAngle) * radius;

  const endX = cx + Math.cos(endAngle) * radius;
  const endY = cy + Math.sin(endAngle) * radius;

  return {
    stroke: {
      kind: "arc",
      cx,
      cy,
      r: radius,
      startAngle,
      endAngle,
      ccw,
    },
    endX,
    endY,
  };
}

/**
 * Build a fractional-octant arc (opcode 0x0B).
 *
 * Layout (5 bytes, unsigned):
 * - byte 0: start offset (0..255) — fraction of 45° added to the
 *   octant start angle: `deltaStart = startOffset * 45° / 256`.
 * - byte 1: end offset (0..255) — fraction of 45° subtracted from the
 *   octant end angle: `deltaEnd = endOffset * 45° / 256`.
 * - byte 2: high byte of radius.
 * - byte 3: low byte of radius. Full radius = `(hi << 8) | lo`.
 * - byte 4: octant byte (same layout as 0x0A).
 */
export function buildFractionalArc(
  startX: number,
  startY: number,
  startOffset: number,
  endOffset: number,
  radiusHigh: number,
  radiusLow: number,
  octByte: number,
  scale: number,
): { stroke: ShxStroke & { kind: "arc" }; endX: number; endY: number } {
  const radius = ((radiusHigh << 8) | radiusLow) * scale;
  const ccw = (octByte & 0x80) !== 0;
  const spanOctants = (octByte & 0x70) >> 4 || 8;
  const startOctant = octByte & 0x07;

  const deltaStart = (startOffset * Math.PI) / (4 * 256);
  const deltaEnd = (endOffset * Math.PI) / (4 * 256);

  const rawStart = (startOctant * Math.PI) / 4 + deltaStart;
  const rawSpan = (spanOctants * Math.PI) / 4 - deltaStart - deltaEnd;

  const startAngle = rawStart;
  const endAngle = ccw ? rawStart + rawSpan : rawStart - rawSpan;

  const cx = startX - Math.cos(startAngle) * radius;
  const cy = startY - Math.sin(startAngle) * radius;

  const endX = cx + Math.cos(endAngle) * radius;
  const endY = cy + Math.sin(endAngle) * radius;

  return {
    stroke: {
      kind: "arc",
      cx,
      cy,
      r: radius,
      startAngle,
      endAngle,
      ccw,
    },
    endX,
    endY,
  };
}

/**
 * Build a bulge arc from two endpoints plus bulge factor.
 *
 * `bulge` is tan(θ/4) where θ is the included angle of the arc, with
 * sign indicating direction (positive = CCW from p1 to p2). This
 * matches the AutoCAD LWPOLYLINE bulge convention and by extension the
 * SHX 0x0C/0x0D opcodes (which store bulge as int8/127).
 */
export function bulgeToArc(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  bulge: number,
): ShxStroke & { kind: "arc" } {
  const chordX = x2 - x1;
  const chordY = y2 - y1;
  const chordLen = Math.hypot(chordX, chordY);
  if (chordLen === 0) {
    // Degenerate — return a zero-radius arc; caller treats as a line.
    return {
      kind: "arc",
      cx: x1,
      cy: y1,
      r: 0,
      startAngle: 0,
      endAngle: 0,
      ccw: bulge >= 0,
    };
  }
  const sagitta = (bulge * chordLen) / 2;
  const radius = Math.abs(
    (chordLen * chordLen + 4 * sagitta * sagitta) / (8 * sagitta),
  );
  // Mid-point of chord
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  // Unit normal to chord, rotated 90° CCW from chord direction
  const nx = -chordY / chordLen;
  const ny = chordX / chordLen;
  // Distance from midpoint to centre
  const halfChord = chordLen / 2;
  const centreDist = Math.sqrt(
    Math.max(0, radius * radius - halfChord * halfChord),
  );
  // Sign of bulge picks which side the centre is on
  const sign = bulge >= 0 ? 1 : -1;
  const cx = midX - sign * nx * centreDist;
  const cy = midY - sign * ny * centreDist;
  const startAngle = Math.atan2(y1 - cy, x1 - cx);
  const endAngle = Math.atan2(y2 - cy, x2 - cx);
  return {
    kind: "arc",
    cx,
    cy,
    r: radius,
    startAngle,
    endAngle,
    ccw: bulge >= 0,
  };
}

/**
 * Skip one opcode plus any immediate operand bytes. Used by the
 * vertical-only flag (0x0E) to cleanly bypass the following
 * instruction in horizontal mode without double-interpreting it.
 *
 * Returns the index **after** the skipped instruction (caller should
 * loop with `i = skipOneOpcode(defBytes, i + 1)` from the position of
 * the 0x0E opcode itself).
 */
function skipOneOpcode(defBytes: Uint8Array, i: number): number {
  if (i >= defBytes.length) return defBytes.length;
  const op = defBytes[i] as number;
  if (
    op === 0x00 ||
    op === 0x01 ||
    op === 0x02 ||
    op === 0x05 ||
    op === 0x06 ||
    op === 0x0e
  ) {
    return i + 1;
  }
  if (op === 0x03 || op === 0x04) return i + 2;
  if (op === 0x07) return i + 3;
  if (op === 0x08) return i + 3;
  if (op === 0x09) {
    // sequence: advance pairs until (0,0) terminator
    let j = i + 1;
    while (j + 1 < defBytes.length) {
      const dx = defBytes[j] as number;
      const dy = defBytes[j + 1] as number;
      j += 2;
      if (dx === 0 && dy === 0) return j;
    }
    return defBytes.length;
  }
  if (op === 0x0a) return i + 3;
  if (op === 0x0b) return i + 6;
  if (op === 0x0c) return i + 4;
  if (op === 0x0d) {
    let j = i + 1;
    while (j + 1 < defBytes.length) {
      const dx = defBytes[j] as number;
      const dy = defBytes[j + 1] as number;
      if (dx === 0 && dy === 0) return j + 2;
      j += 3;
    }
    return defBytes.length;
  }
  if (op === 0x0f) return i + 2;
  // Direction-length vector (0x10-0xFF) is a single byte.
  return i + 1;
}

/**
 * Interpret a raw SHX shape-definition byte stream into strokes.
 *
 * `knownGlyphs` is the set of glyphs parsed earlier in the file —
 * subshapes (opcode 0x07) resolve against it. Forward references are
 * unsupported (AutoCAD's single-pass parser has the same restriction).
 *
 * `formatVersion` selects opcode semantics (see module-level table):
 * currently only 0x0B (fractional arc) differs between 1.0 and 2.0.
 * Defaults to 2 for backward compatibility with callers written
 * before the 1.0 format was added.
 *
 * `depth` tracks recursion across subshape lookups. The limit of 10
 * mirrors AutoCAD's own safety valve and catches pathological cycles.
 */
export function interpretShape(
  codepoint: number,
  defBytes: Uint8Array,
  knownGlyphs: Map<number, ShxGlyph>,
  formatVersion: ShxFormatVersion = 2,
  depth = 0,
): ShxGlyph {
  if (depth > 10) {
    throw interpreterError(
      "subshape-cycle",
      `subshape recursion depth exceeded (code ${codepoint})`,
    );
  }

  let x = 0;
  let y = 0;
  let penDown = true;
  let scale = 1;
  /** Mode: 0 = horizontal (default), 1 = vertical. Unifont 2.0 in mode-0 fonts stays horizontal. */
  const writingMode = 0;
  const stack: Array<{ x: number; y: number }> = [];
  const strokes: ShxStroke[] = [];

  const readByte = (i: number): number => {
    if (i >= defBytes.length) {
      throw interpreterError(
        "truncated",
        `shape ${codepoint} ended mid-opcode at byte ${i}`,
      );
    }
    return defBytes[i] as number;
  };

  let i = 0;
  while (i < defBytes.length) {
    const op = defBytes[i] as number;
    if (op === SHX_OP.END) {
      break;
    } else if (op === SHX_OP.PEN_DOWN) {
      penDown = true;
      i += 1;
    } else if (op === SHX_OP.PEN_UP) {
      penDown = false;
      i += 1;
    } else if (op === SHX_OP.SCALE_DIVIDE) {
      const divisor = readByte(i + 1);
      if (divisor === 0) {
        throw interpreterError(
          "bad-shape",
          `shape ${codepoint}: scale divisor is 0`,
        );
      }
      scale /= divisor;
      i += 2;
    } else if (op === SHX_OP.SCALE_MULTIPLY) {
      const multiplier = readByte(i + 1);
      scale *= multiplier;
      i += 2;
    } else if (op === SHX_OP.PUSH) {
      stack.push({ x, y });
      i += 1;
    } else if (op === SHX_OP.POP) {
      const snap = stack.pop();
      if (!snap) {
        throw interpreterError(
          "bad-shape",
          `shape ${codepoint}: stack underflow on pop`,
        );
      }
      x = snap.x;
      y = snap.y;
      i += 1;
    } else if (op === SHX_OP.SUBSHAPE) {
      const hi = readByte(i + 1);
      const lo = readByte(i + 2);
      const subCode = (hi << 8) | lo;
      const sub = knownGlyphs.get(subCode);
      if (!sub) {
        throw interpreterError(
          "bad-shape",
          `shape ${codepoint}: subshape ${subCode} not found`,
        );
      }
      for (const stroke of sub.strokes) {
        strokes.push(translateAndScaleStroke(stroke, x, y, scale));
      }
      x += sub.advanceX * scale;
      y += sub.advanceY * scale;
      i += 3;
    } else if (op === SHX_OP.DISPLACEMENT) {
      const dx = toSignedInt8(readByte(i + 1)) * scale;
      const dy = toSignedInt8(readByte(i + 2)) * scale;
      if (penDown) {
        strokes.push({ kind: "line", x1: x, y1: y, x2: x + dx, y2: y + dy });
      }
      x += dx;
      y += dy;
      i += 3;
    } else if (op === SHX_OP.DISPLACEMENT_SEQ) {
      let j = i + 1;
      while (true) {
        const dxRaw = readByte(j);
        const dyRaw = readByte(j + 1);
        j += 2;
        if (dxRaw === 0 && dyRaw === 0) break;
        const dx = toSignedInt8(dxRaw) * scale;
        const dy = toSignedInt8(dyRaw) * scale;
        if (penDown) {
          strokes.push({
            kind: "line",
            x1: x,
            y1: y,
            x2: x + dx,
            y2: y + dy,
          });
        }
        x += dx;
        y += dy;
      }
      i = j;
    } else if (op === SHX_OP.OCTANT_ARC) {
      const radius = readByte(i + 1) * scale;
      const octByte = readByte(i + 2);
      const arc = buildOctantArc(x, y, radius, octByte);
      if (penDown) {
        strokes.push(arc.stroke);
      }
      x = arc.endX;
      y = arc.endY;
      i += 3;
    } else if (op === SHX_OP.FRACTIONAL_ARC) {
      if (formatVersion === 2) {
        const startOffset = readByte(i + 1);
        const endOffset = readByte(i + 2);
        const radiusHigh = readByte(i + 3);
        const radiusLow = readByte(i + 4);
        const octByte = readByte(i + 5);
        const arc = buildFractionalArc(
          x,
          y,
          startOffset,
          endOffset,
          radiusHigh,
          radiusLow,
          octByte,
          scale,
        );
        if (penDown) {
          strokes.push(arc.stroke);
        }
        x = arc.endX;
        y = arc.endY;
        i += 6;
      } else {
        // v1.0: 0x0B is reserved. A well-formed 1986-era font will not
        // emit it; if we encounter it, we can't know the intended
        // operand layout. Skip the opcode byte conservatively and keep
        // going so a single stray byte does not poison the whole font.
        // (A file that genuinely contains 0x0B is almost certainly
        // corrupted or mislabeled — fail-soft matches AutoCAD's own
        // tolerant behaviour on legacy fonts.)
        i += 1;
      }
    } else if (op === SHX_OP.BULGE_ARC) {
      const dx = toSignedInt8(readByte(i + 1)) * scale;
      const dy = toSignedInt8(readByte(i + 2)) * scale;
      const bulgeRaw = toSignedInt8(readByte(i + 3));
      const bulge = bulgeRaw / 127;
      const endX = x + dx;
      const endY = y + dy;
      if (penDown) {
        if (bulge === 0) {
          strokes.push({ kind: "line", x1: x, y1: y, x2: endX, y2: endY });
        } else {
          strokes.push(bulgeToArc(x, y, endX, endY, bulge));
        }
      }
      x = endX;
      y = endY;
      i += 4;
    } else if (op === SHX_OP.POLYLINE_ARC) {
      let j = i + 1;
      while (true) {
        const dxRaw = readByte(j);
        const dyRaw = readByte(j + 1);
        if (dxRaw === 0 && dyRaw === 0) {
          j += 2;
          break;
        }
        const bulgeRaw = toSignedInt8(readByte(j + 2));
        const bulge = bulgeRaw / 127;
        const dx = toSignedInt8(dxRaw) * scale;
        const dy = toSignedInt8(dyRaw) * scale;
        const endX = x + dx;
        const endY = y + dy;
        if (penDown) {
          if (bulge === 0) {
            strokes.push({ kind: "line", x1: x, y1: y, x2: endX, y2: endY });
          } else {
            strokes.push(bulgeToArc(x, y, endX, endY, bulge));
          }
        }
        x = endX;
        y = endY;
        j += 3;
      }
      i = j;
    } else if (op === SHX_OP.VERTICAL_ONLY) {
      // In a horizontal-mode font (or any time the writing direction is
      // horizontal) the vertical-only flag suppresses the next opcode.
      // In a vertical-mode font it would suppress the op when writing
      // horizontally instead; since we only support mode-0 fonts here
      // the heuristic reduces to: writingMode === 0 → skip.
      if (writingMode === 0) {
        i = skipOneOpcode(defBytes, i + 1);
      } else {
        i += 1;
      }
    } else if (op === SHX_OP.RESERVED_0F) {
      // Reserved in unifont 2.0; conservatively consume 1 operand byte
      // if present so forward-compatible data is ignored cleanly.
      i += 2;
    } else if (op >= 0x10) {
      // Direction-length vector.
      const len = (op >> 4) * scale;
      const dirIndex = op & 0x0f;
      const dirAngle = (dirIndex * Math.PI) / 8; // 22.5° per step
      const dx = Math.cos(dirAngle) * len;
      const dy = Math.sin(dirAngle) * len;
      if (penDown) {
        strokes.push({ kind: "line", x1: x, y1: y, x2: x + dx, y2: y + dy });
      }
      x += dx;
      y += dy;
      i += 1;
    } else {
      // Should be unreachable — all byte values 0x00..0xFF are covered.
      throw interpreterError(
        "unknown-opcode",
        `shape ${codepoint}: unknown opcode 0x${op.toString(16)} at byte ${i}`,
      );
    }
  }

  return {
    codepoint,
    strokes,
    advanceX: x,
    advanceY: y,
  };
}
